import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CURRENT_CLIENT_TEMPLATE_VAR } from '@core';
import type { DeviceFullModel, DeviceModel, NewDeviceModelData } from '@devices';
import type { ImageDocument } from '@space-plan/models';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DeviceModelService {
  private http = inject(HttpClient);

  getModels(): Observable<DeviceModel[]> {
    return this.http
      .get<{ data: DeviceModel[]; }>(`devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/device-models`)
      .pipe(map(response => response.data));
  }

  getFilteredModels(filters: Pick<{ deviceMaker?: string[]; deviceType?: string[]; }, 'deviceMaker' | 'deviceType'>) {
    return this.http
      .post<{ data: DeviceModel[]; }>(`devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/device-models/filter`, filters)
      .pipe(map(response => response.data));
  }

  getFullModel({ clientId, manufacturerId, modelId }: { clientId: string; manufacturerId: string; modelId: string; }) {
    return this.http
      .get<{ data: DeviceFullModel; }>(
        `devices/v1/clients/${clientId}/device-makers/${manufacturerId}/device-models/${modelId}`,
      )
      .pipe(map(response => response.data));
  }

  addNewDeviceModel(clientId: string, makeId: string, data: NewDeviceModelData): Observable<DeviceModel> {
    return this.http
      .post<{ data: DeviceModel; }>(`devices/v1/clients/${clientId}/device-makers/${makeId}/device-models`, data)
      .pipe(map(response => response.data));
  }

  updateDeviceModel(config: {
    clientId: string;
    data: NewDeviceModelData;
    makeId: string;
    modelId: string;
  }): Observable<DeviceModel> {
    return this.http
      .patch<{ data: DeviceModel; }>(
        `devices/v1/clients/${config.clientId}/device-makers/${config.makeId}/device-models/${config.modelId}`,
        config.data,
      )
      .pipe(map(response => response.data));
  }

  uploadDocumentToDeviceModel(
    {
      clientId,
      deviceMakeId,
      deviceModelId,
      body,
    }: {
      body: File;
      clientId: string;
      deviceMakeId: string;
      deviceModelId: string;
    },
  ) {
    return this.http
      .put<{ data: ImageDocument; }>(
        `devices/v1/clients/${clientId}/device-makers/${deviceMakeId}/device-models/${deviceModelId}/documents?setAsReferenceDocument=true`,
        body,
        {
          headers: new HttpHeaders({
            'Content-Type': body.type,
            FileName: body.name,
          }),
        },
      )
      .pipe(map(({ data }) => data));
  }

  deleteDeviceModel(config: { makeId: string; modelId: string; }) {
    return this.http
      .delete<{ data: DeviceModel; }>(
        `devices/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/device-makers/${config.makeId}/device-models/${config.modelId}`,
      )
      .pipe(map(response => response.data));
  }
}
